import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Typography,
  Switch,
  Grid,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Delete, ArrowBack, Save } from '@mui/icons-material';
import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import LoadingButton from '@/components/LoadingButton';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import { CustomPaymentTypesApi } from '@/api/clients/custom-payments/custom-payment-types.api';
import CreateRefundConfigSection from './CreateRefundConfigSection';
import Copyable from '@/components/Copyable/Copyable';

const defaultFormValues = {
  name: '',
  allowImmediatePayments: false,
  forceImmediatePayments: false,
  showInCreatePayment: false,
  enablePayeeExceptionWorkflow: false,
  identifiers: [''],
  entityType: 'Individual',
  enableEscheatment: false,
  interestFlag: '',
  propertyType: '',
  ownerTypeCode: '',
  deductionAmount: '',
  additionAmount: '',
  deletionAmount: '',
  approvalManualCreatePayment: false,
  approvalPaymentFile: false,
  approvalApi: false,
  enableResubmissionApprovals: false,
  autoDeclineApprovals: false,
  autoDeclineApprovalDays: '',
  autoSendCheckUnclaimed: false,
  autoSendCheckUnclaimedDays: '',
  enableInternalAttachments: false,
  requireInternalAttachments: false,
  enablePayeeAttachments: false,
  requirePayeeAttachments: false,
  createRefundConfig: [],
};

const CreateOrEditCustomPaymentType = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId');
  const isEditMode = Boolean(id);

  const [formValues, setFormValues] = useState(defaultFormValues);
  const [slug, setSlug] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isEditMode) {
      const fetchCustomPaymentData = async () => {
        setIsLoading(true);
        try {
          const response = await CustomPaymentTypesApi.getCustomPaymentType(
            organizationId,
            id,
          );
          const data = response.data;
          if (data) {
            setSlug(data.slug);
            setFormValues({
              name: data.name || '',
              allowImmediatePayments: data.allowImmediatePayments,
              forceImmediatePayments: data.forceImmediatePayments,
              showInCreatePayment: data.showInCreatePayment,
              enablePayeeExceptionWorkflow: data.enablePayeeExceptionWorkflow,
              identifiers:
                data.identifiers && data.identifiers.length > 0
                  ? data.identifiers
                  : [''],
              entityType: data.entityType,
              enableEscheatment: data.enableEscheatment,
              interestFlag: data.interestFlag,
              propertyType: data.propertyType,
              ownerTypeCode: data.ownerTypeCode,
              deductionAmount: data.deductionAmount,
              additionAmount: data.additionAmount,
              deletionAmount: data.deletionAmount,
              approvalManualCreatePayment: data.approvalManualCreatePayment,
              approvalPaymentFile: data.approvalPaymentFile,
              approvalApi: data.approvalApi,
              enableResubmissionApprovals: data.enableResubmissionApprovals,
              autoDeclineApprovals: data.autoDeclineApprovals,
              autoDeclineApprovalDays: data.autoDeclineApprovalDays,
              autoSendCheckUnclaimed: data.autoSendCheckUnclaimed,
              autoSendCheckUnclaimedDays: data.autoSendCheckUnclaimedDays,
              enableInternalAttachments:
                data.enableInternalAttachments || false,
              requireInternalAttachments:
                data.requireInternalAttachments || false,
              enablePayeeAttachments: data.enablePayeeAttachments || false,
              requirePayeeAttachments: data.requirePayeeAttachments || false,
              createRefundConfig: data.createRefundConfig || [],
            });
          }
        } catch (error) {
          console.error('Error fetching custom payment type:', error);
          setErrorMessage('Failed to load custom payment type data.');
          setShowErrorAlert(true);
        } finally {
          setIsLoading(false);
        }
      };
      fetchCustomPaymentData();
    }
  }, [id, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      const fetchDefaultConfig = async () => {
        try {
          const response = await CustomPaymentTypesApi.getDefaultCreateRefundConfig();
          if (response.data) {
            setFormValues((prev) => ({
              ...prev,
              createRefundConfig: response.data,
            }));
          }
        } catch (error) {
          console.error('Error fetching default create refund config:', error);
        }
      };
      fetchDefaultConfig();
    }
  }, [isEditMode]);

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleToggleChange = (field) => (event) => {
    handleInputChange(field, event.target.checked);
  };

  const handleIdentifierChange = (index, value) => {
    const newIdentifiers = [...formValues.identifiers];
    newIdentifiers[index] = value;
    handleInputChange('identifiers', newIdentifiers);
  };

  const handleAddIdentifier = () => {
    handleInputChange('identifiers', [...formValues.identifiers, '']);
  };

  const handleRemoveIdentifier = (index) => {
    if (formValues.identifiers.length <= 1) {
      return;
    }
    const newIdentifiers = formValues.identifiers.filter((_, i) => i !== index);
    handleInputChange('identifiers', newIdentifiers);
  };

  const handleEntityTypeChange = (event, newValue) => {
    if (newValue !== null) {
      handleInputChange('entityType', newValue);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formValues.name || formValues.name.trim() === '') {
      newErrors.name = 'Custom Payment Type Name is required';
    }

    formValues.identifiers.forEach((identifier, index) => {
      if (!identifier || identifier.trim() === '') {
        newErrors[`identifier_${index}`] = 'Identifier cannot be empty';
      }
    });

    if (formValues.enableEscheatment) {
      const amountFields = [
        'additionAmount',
        'deductionAmount',
        'deletionAmount',
      ];
      amountFields.forEach((field) => {
        const value = formValues[field];
        if (value && isNaN(Number(value))) {
          newErrors[field] = 'Must be a valid number';
        }
        if (value && Number(value) < 0) {
          newErrors[field] = 'Cannot be negative';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setErrorMessage('Please fix the errors in the form before submitting.');
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 5000);
      return;
    }

    setIsSaving(true);
    try {
      const cleanedIdentifiers = formValues.identifiers.filter(
        (id) => id && id.trim() !== '',
      );

      const payload = {
        name: formValues.name,
        identifiers: cleanedIdentifiers,
        entityType: formValues.entityType,
        allowImmediatePayments: formValues.allowImmediatePayments,
        forceImmediatePayments: formValues.forceImmediatePayments,
        showInCreatePayment: formValues.showInCreatePayment,
        enablePayeeExceptionWorkflow: formValues.enablePayeeExceptionWorkflow,
        enableEscheatment: formValues.enableEscheatment,
        interestFlag: formValues.interestFlag || null,
        propertyType: formValues.propertyType || null,
        ownerTypeCode: formValues.ownerTypeCode || null,
        deductionAmount: formValues.deductionAmount || null,
        additionAmount: formValues.additionAmount || null,
        deletionAmount: formValues.deletionAmount || null,
        approvalManualCreatePayment: formValues.approvalManualCreatePayment,
        approvalPaymentFile: formValues.approvalPaymentFile,
        approvalApi: formValues.approvalApi,
        enableResubmissionApprovals: formValues.enableResubmissionApprovals,
        autoDeclineApprovals: formValues.autoDeclineApprovals,
        autoDeclineApprovalDays: formValues.autoDeclineApprovalDays || null,
        autoSendCheckUnclaimed: formValues.autoSendCheckUnclaimed,
        autoSendCheckUnclaimedDays:
          formValues.autoSendCheckUnclaimedDays || null,
        enableInternalAttachments: formValues.enableInternalAttachments,
        requireInternalAttachments: formValues.requireInternalAttachments,
        enablePayeeAttachments: formValues.enablePayeeAttachments,
        requirePayeeAttachments: formValues.requirePayeeAttachments,
        createRefundConfig: formValues.createRefundConfig,
      };

      if (isEditMode) {
        await CustomPaymentTypesApi.updateCustomPaymentType(
          organizationId,
          id,
          payload,
        );
      } else {
        await CustomPaymentTypesApi.createCustomPaymentType(
          organizationId,
          payload,
        );
      }

      setShowSuccessAlert(true);
      setTimeout(() => {
        if (organizationId) {
          navigate(
            `/admin/updateOrganization/${organizationId}#custom-payment-types`,
          );
        } else {
          navigate('/admin/custom-payment-types');
        }
      }, 1500);
    } catch (error) {
      console.error('Error saving custom payment type:', error);

      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        const apiErrors = {};
        const errorMessages = [];

        error.response.data.errors.forEach((err) => {
          if (err.path) {
            apiErrors[err.path] = err.msg;
          }
          errorMessages.push(err.msg);
        });

        setErrors((prev) => ({ ...prev, ...apiErrors }));
        setErrorMessage(errorMessages.join('; '));
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            error.message ||
            'Failed to save custom payment type. Please try again.',
        );
      }

      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (organizationId) {
      navigate(
        `/admin/updateOrganization/${organizationId}#custom-payment-types`,
      );
    } else {
      navigate('/admin/custom-payment-types');
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      height='auto'
      header={
        <CardHeader color='primary'>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={handleCancel}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <ArrowBack sx={{ color: 'white' }} />
            </IconButton>
            <h4 style={{ margin: 0, color: 'white' }}>
              {isEditMode
                ? 'Edit Custom Payment Type'
                : 'Create Custom Payment Type'}
            </h4>
          </Box>
        </CardHeader>
      }>
      <Container maxWidth='md' sx={{ py: 3 }}>
        {showSuccessAlert && (
          <Alert severity='success' sx={{ mb: 3 }}>
            Custom payment type {isEditMode ? 'updated' : 'created'}{' '}
            successfully!
          </Alert>
        )}

        {showErrorAlert && (
          <Alert
            severity='error'
            onClose={() => setShowErrorAlert(false)}
            sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            label='Custom Payment Type Name'
            value={formValues.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
            size='small'
          />
          {isEditMode && slug && (
            <Box sx={{ mt: 2 }}>
              <Copyable value={slug}>
                <TextField
                  fullWidth
                  label='Slug'
                  value={slug}
                  size='small'
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: 'text.secondary',
                    },
                  }}
                />
              </Copyable>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3, mb: 3 }}>
          <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
            Settings
          </Typography>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2'>Allow Immediate Payments</Typography>
              <InfoTooltip title='Enables ability to choose immediate payments as an option in Create Payments and Resolution Center' />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}>
              <Switch
                checked={formValues.allowImmediatePayments}
                onChange={handleToggleChange('allowImmediatePayments')}
                color='primary'
              />
            </Grid>

            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2'>Force Immediate Payments</Typography>
              <InfoTooltip title='Forces this payment to default to Check Regular workflow via check printing' />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}>
              <Switch
                checked={formValues.forceImmediatePayments}
                onChange={handleToggleChange('forceImmediatePayments')}
                color='primary'
              />
            </Grid>

            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2'>
                Allowed in create payment (display in create payment)
              </Typography>
              <InfoTooltip title='Enables the custom payment type to be selected in the create payment page' />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}>
              <Switch
                checked={formValues.showInCreatePayment}
                onChange={handleToggleChange('showInCreatePayment')}
                color='primary'
              />
            </Grid>

            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2'>Payee Exception Workflow</Typography>
              <InfoTooltip title='Enables the exception workflow that will be managed in data mapping for this custom payment' />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}>
              <Switch
                checked={formValues.enablePayeeExceptionWorkflow}
                onChange={handleToggleChange('enablePayeeExceptionWorkflow')}
                color='primary'
              />
            </Grid>
            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2'>
                Resubmission for Manual Approval
              </Typography>
              <InfoTooltip title='If enabled the custom payment if declined by a user can be resubmitted via the Approvals page' />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}>
              <Switch
                checked={formValues.enableResubmissionApprovals}
                onChange={handleToggleChange('enableResubmissionApprovals')}
                color='primary'
              />
            </Grid>

            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2'>
                Auto decline for Manual Approval
              </Typography>
              <InfoTooltip title='Once enabled and number of days set will trigger an auto decline from status Awaiting for Approval' />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1,
              }}>
              <TextField
                label='Days'
                type='number'
                size='small'
                value={formValues.autoDeclineApprovalDays}
                onChange={(e) =>
                  handleInputChange(
                    'autoDeclineApprovalDays',
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
                disabled={!formValues.autoDeclineApprovals}
                error={Boolean(
                  errors.autoDeclineApprovals || errors.autoDeclineApprovalDays,
                )}
                sx={{ width: '80px' }}
              />
              <Switch
                checked={formValues.autoDeclineApprovals}
                onChange={handleToggleChange('autoDeclineApprovals')}
                color='primary'
              />
            </Grid>

            <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2'>
                Unclaimed payment records set to physical check
              </Typography>
              <InfoTooltip title='Once enabled and number of days set will trigger payment record to have a check sent out to an available address' />
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1,
              }}>
              <TextField
                label='Days'
                type='number'
                size='small'
                value={formValues.autoSendCheckUnclaimedDays}
                onChange={(e) =>
                  handleInputChange(
                    'autoSendCheckUnclaimedDays',
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
                disabled={!formValues.autoSendCheckUnclaimed}
                error={Boolean(
                  errors.autoSendCheckUnclaimed ||
                    errors.autoSendCheckUnclaimedDays,
                )}
                sx={{ width: '80px' }}
              />
              <Switch
                checked={formValues.autoSendCheckUnclaimed}
                onChange={handleToggleChange('autoSendCheckUnclaimed')}
                color='primary'
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 3, mb: 3 }}>
          <Typography variant='subtitle1' sx={{ mb: 3, fontWeight: 600 }}>
            Payment Attachments
          </Typography>

          <Box sx={{ mb: 3, pl: 2, borderLeft: '3px solid #3ab895' }}>
            <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 600 }}>
              Enable attachment of documents for internal use
            </Typography>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='body2'>
                  Enable internal document attachments
                </Typography>
                <InfoTooltip title='Files for internal use only, will not be transmitted to payees or appear in payee portal.' />
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}>
                <Switch
                  checked={formValues.enableInternalAttachments}
                  onChange={handleToggleChange('enableInternalAttachments')}
                  color='primary'
                />
              </Grid>

              <Grid
                item
                xs={8}
                sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                <Typography
                  variant='body2'
                  color={
                    !formValues.enableInternalAttachments
                      ? 'text.disabled'
                      : 'text.primary'
                  }>
                  Required document attachment
                </Typography>
                <InfoTooltip title='When enabled, users must attach at least one internal document when creating a payment' />
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}>
                <Switch
                  checked={formValues.requireInternalAttachments}
                  onChange={handleToggleChange('requireInternalAttachments')}
                  disabled={!formValues.enableInternalAttachments}
                  color='primary'
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ pl: 2, borderLeft: '3px solid #3ab895' }}>
            <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 600 }}>
              Enable attachment of documents to be sent to payee
            </Typography>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='body2'>
                  Enable payee document attachments
                </Typography>
                <InfoTooltip title='Files to be sent to payee, will be transmitted to payees and appear in the payee portal.' />
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}>
                <Switch
                  checked={formValues.enablePayeeAttachments}
                  onChange={handleToggleChange('enablePayeeAttachments')}
                  color='primary'
                />
              </Grid>

              <Grid
                item
                xs={8}
                sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                <Typography
                  variant='body2'
                  color={
                    !formValues.enablePayeeAttachments
                      ? 'text.disabled'
                      : 'text.primary'
                  }>
                  Required document attachment
                </Typography>
                <InfoTooltip title='When enabled, users must attach at least one payee document when creating a payment' />
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}>
                <Switch
                  checked={formValues.requirePayeeAttachments}
                  onChange={handleToggleChange('requirePayeeAttachments')}
                  disabled={!formValues.enablePayeeAttachments}
                  color='primary'
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            p: 3,
            mb: 3,
          }}>
          <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
            Payment Category Identifiers
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {formValues.identifiers.map((identifier, index) => (
              <Grid container spacing={2} key={index} alignItems='center'>
                <Grid item xs={10}>
                  <TextField
                    fullWidth
                    label={`Identifier ${index + 1}`}
                    placeholder='Value'
                    value={identifier}
                    onChange={(e) =>
                      handleIdentifierChange(index, e.target.value)
                    }
                    error={Boolean(errors[`identifier_${index}`])}
                    helperText={errors[`identifier_${index}`]}
                    size='small'
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    onClick={() => handleRemoveIdentifier(index)}
                    color='error'
                    disabled={formValues.identifiers.length <= 1}
                    sx={{
                      backgroundColor: 'error.light',
                      '&:hover': { backgroundColor: 'error.main' },
                      '&.Mui-disabled': { backgroundColor: 'grey.300' },
                    }}>
                    <Delete sx={{ color: 'white' }} />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              variant='outlined'
              color='success'
              startIcon={<Add />}
              onClick={handleAddIdentifier}
              sx={{ alignSelf: 'flex-start' }}>
              Add Identifier
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 3, mb: 3 }}>
          <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
            Entity Type
          </Typography>
          <ToggleButtonGroup
            value={formValues.entityType}
            exclusive
            onChange={handleEntityTypeChange}
            aria-label='entity type'
            fullWidth>
            <ToggleButton value='Individual' aria-label='individual'>
              Individual
            </ToggleButton>
            <ToggleButton value='Business' aria-label='business'>
              Business
            </ToggleButton>
            <ToggleButton value='Unknown' aria-label='unknown' disabled>
              Unknown
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            p: 3,
            mb: 3,
          }}>
          <Grid container sx={{ mb: 2 }}>
            <Grid item xs={8}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                Enable Escheatment
              </Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Switch
                checked={formValues.enableEscheatment}
                onChange={handleToggleChange('enableEscheatment')}
                color='primary'
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Interest Flag'
                value={formValues.interestFlag}
                onChange={(e) =>
                  handleInputChange('interestFlag', e.target.value)
                }
                disabled={!formValues.enableEscheatment}
                size='small'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='property_type'
                value={formValues.propertyType}
                onChange={(e) =>
                  handleInputChange('propertyType', e.target.value)
                }
                disabled={!formValues.enableEscheatment}
                size='small'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='owner_type_code'
                value={formValues.ownerTypeCode}
                onChange={(e) =>
                  handleInputChange('ownerTypeCode', e.target.value)
                }
                disabled={!formValues.enableEscheatment}
                size='small'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='deduction_amount'
                value={formValues.deductionAmount}
                onChange={(e) =>
                  handleInputChange('deductionAmount', e.target.value)
                }
                disabled={!formValues.enableEscheatment}
                error={Boolean(errors.deductionAmount)}
                helperText={errors.deductionAmount}
                size='small'
                type='number'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='addition_amount'
                value={formValues.additionAmount}
                onChange={(e) =>
                  handleInputChange('additionAmount', e.target.value)
                }
                disabled={!formValues.enableEscheatment}
                error={Boolean(errors.additionAmount)}
                helperText={errors.additionAmount}
                size='small'
                type='number'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='deletion_amount'
                value={formValues.deletionAmount}
                onChange={(e) =>
                  handleInputChange('deletionAmount', e.target.value)
                }
                disabled={!formValues.enableEscheatment}
                error={Boolean(errors.deletionAmount)}
                helperText={errors.deletionAmount}
                size='small'
                type='number'
              />
            </Grid>
          </Grid>
        </Box>

        {formValues.createRefundConfig.length > 0 && (
          <CreateRefundConfigSection
            config={formValues.createRefundConfig}
            onChange={(updatedConfig) =>
              handleInputChange('createRefundConfig', updatedConfig)
            }
          />
        )}

        <Box
          sx={{
            p: 3,
            mb: 3,
          }}>
          <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
            Use Approval workflow
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <Typography variant='body2'>Manual Create Payment</Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Switch
                checked={formValues.approvalManualCreatePayment}
                onChange={handleToggleChange('approvalManualCreatePayment')}
                color='primary'
              />
            </Grid>

            <Grid item xs={8}>
              <Typography variant='body2'>Payment File</Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Switch
                checked={formValues.approvalPaymentFile}
                onChange={handleToggleChange('approvalPaymentFile')}
                color='primary'
              />
            </Grid>

            <Grid item xs={8}>
              <Typography variant='body2'>API</Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Switch
                checked={formValues.approvalApi}
                onChange={handleToggleChange('approvalApi')}
                color='primary'
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mt: 3,
          }}>
          <Button
            variant='outlined'
            onClick={handleCancel}
            disabled={isSaving}
            sx={{ height: '42px' }}>
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            color='primary'
            startIcon={<Save sx={{ color: 'white' }} />}
            onClick={handleSubmit}
            isLoading={isSaving}
            sx={{ height: '42px' }}>
            Save
          </LoadingButton>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default CreateOrEditCustomPaymentType;
